import { QueryKey } from "@tanstack/react-query"
import { AxiosResponse } from "axios"
import _ from "lodash"

import {
  CUSTOMISATION_CATEGORIES,
  CustomisationCategory,
  CustomisationCategoryWithoutNone,
  SETTINGS_SCOPE,
} from "@tfg/types/constants/communication-studio.constant"
import {
  CategorisedConfigurations,
  ComponentCustomisationConfigurationMapper,
  Configurations,
} from "@tfg/types/schemas/communication-studio/configurationParameters"
import {
  COMPONENT_REGISTRY,
  ComponentMetadata,
} from "@tfg/types/types/communication-studio/components.types"
import {
  ConfigurationsScopeType,
  ScopedConfigurationsResponse,
} from "@tfg/types/types/communication-studio/configurations.types"
import { StylingParameters } from "@tfg/types/types/communication-studio/styling-parameters.types"
import { DEFAULT_CONFIGURATIONS, DEFAULT_STYLING_PARAMETERS } from "@tfg/ui"

import { queryClient } from "~/backend/queryClient"
import {
  GetDesignTokensErrorResponseDto,
  GetDesignTokensQueryResponse,
  GetProductPageComponentsResponseDto,
} from "~/gen/api"

export const getDesignTokensQueryResult = (queryKey: QueryKey) => {
  const result =
    queryClient.getQueryData<AxiosResponse<GetDesignTokensQueryResponse>>(
      queryKey
    )
  if (!result) return undefined
  return getDesignTokensFromQueryResponse(result.data)
}

export const getDesignTokensFromQueryResponse = (
  response:
    | GetDesignTokensQueryResponse
    | GetDesignTokensErrorResponseDto
    | undefined
):
  | {
      stylingParameters: StylingParameters
      configurations: ScopedConfigurationsResponse
    }
  | undefined => {
  if (!response || !response.ok || !("data" in response)) {
    const reason = response
      ? (response as GetDesignTokensErrorResponseDto).reason
      : "No response"
    console.error("Error getting design tokens from query response", reason)
    return undefined
  }
  const designTokens = response.data
  return {
    stylingParameters: {
      ...DEFAULT_STYLING_PARAMETERS,
      ...designTokens.stylingParameters.categories,
    },
    configurations: {
      [SETTINGS_SCOPE.COMPONENT_CUSTOMISATION]: {
        ...DEFAULT_CONFIGURATIONS[SETTINGS_SCOPE.COMPONENT_CUSTOMISATION],
        ...designTokens.configurations[SETTINGS_SCOPE.COMPONENT_CUSTOMISATION],
      },
      [SETTINGS_SCOPE.CONTENT_FILTERING]:
        designTokens.configurations[SETTINGS_SCOPE.CONTENT_FILTERING],
    },
  }
}

export const isConfigurationCategory = (
  category: CustomisationCategory
): category is keyof typeof ComponentCustomisationConfigurationMapper => {
  return category in ComponentCustomisationConfigurationMapper
}

export const getConfigurationSchema = (category: CustomisationCategory) => {
  if (isConfigurationCategory(category)) {
    return ComponentCustomisationConfigurationMapper[category]
  }
  return undefined
}

export const createCategorisedCustomisationObjectFromCategories = (
  categories: Configurations | undefined
): CategorisedConfigurations | undefined => {
  if (!categories) return undefined
  return {
    name: SETTINGS_SCOPE.COMPONENT_CUSTOMISATION,
    type: ConfigurationsScopeType.CATEGORISED,
    categories,
  }
}

export const getComponentMetadata = (
  componentType: GetProductPageComponentsResponseDto["componentType"]
): ComponentMetadata => {
  return COMPONENT_REGISTRY[componentType]
}

export const getAllCategoriesForComponent = (
  componentType: GetProductPageComponentsResponseDto["componentType"]
): CustomisationCategory[] => {
  const metadata = getComponentMetadata(componentType)
  if (!metadata) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`No metadata found for component type: ${componentType}`)
    }
    return []
  }
  return [CUSTOMISATION_CATEGORIES.GLOBAL, ...metadata.customisationCategories]
}

export const getAvailableCategoriesFromComponents = (
  components: GetProductPageComponentsResponseDto[]
): CustomisationCategory[] => {
  return [
    ...new Set(
      components.flatMap((component) => {
        const metadata = getComponentMetadata(component.componentType)
        if (!metadata || !metadata.customisationCategories) return []
        return metadata.customisationCategories
      })
    ),
  ]
}

export const getOrderedCategories = (
  components: GetProductPageComponentsResponseDto[]
): CustomisationCategoryWithoutNone[] => {
  if (_.isEmpty(components)) {
    return []
  }
  const categoriesInOrder: CustomisationCategoryWithoutNone[] = []
  const seenCategories = new Set<CustomisationCategoryWithoutNone>()

  if (!seenCategories.has(CUSTOMISATION_CATEGORIES.GLOBAL)) {
    categoriesInOrder.push(CUSTOMISATION_CATEGORIES.GLOBAL)
    seenCategories.add(CUSTOMISATION_CATEGORIES.GLOBAL)
  }

  for (const component of components) {
    const metadata = getComponentMetadata(component.componentType)
    if (!metadata) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `No metadata found for component type: ${component.componentType}`
        )
      }
      continue
    }
    for (const category of metadata.customisationCategories) {
      if (
        category === CUSTOMISATION_CATEGORIES.NONE ||
        category === CUSTOMISATION_CATEGORIES.GLOBAL ||
        category === CUSTOMISATION_CATEGORIES.SEARCH_ENGINE
      ) {
        continue
      }
      if (!seenCategories.has(category)) {
        categoriesInOrder.push(category)
        seenCategories.add(category)
      }
    }
  }

  if (!seenCategories.has(CUSTOMISATION_CATEGORIES.SEARCH_ENGINE)) {
    categoriesInOrder.push(CUSTOMISATION_CATEGORIES.SEARCH_ENGINE)
    seenCategories.add(CUSTOMISATION_CATEGORIES.SEARCH_ENGINE)
  }

  return categoriesInOrder
}
